import { store } from '@betacrew/jars-flux';
import { all, call, put, takeLatest, takeLeading } from 'redux-saga/effects';
import General from '../../../../../libs/utility/General';
import ReconciliationDataService from '../../../../../services/ReconciliationDataService';
import { TOASTER_VARIANT } from '../../../../components/toast/constants';
import { PAGE_STATE } from '../../../constants/constants';
import { addNotifications } from '../../../toaster/ToasterPortalSlice';
import { setReconciliationSelectedFormRecords, setReconciliationSelectedState } from '../ReconciliationSlice';

import { componentKey, setIsSelectedFormsCombinationAllowed, setLoadingState, setPayerDropdownOptions, setReconciliationFormsListRecords, setReconciliationStateDropdownOptions, setTaxYearDropdownOptions, updatePaginationState } from './ReconciliationListingSlice';
import { RECONCILIATION_LOADING_STATES } from '../constants';

export const { getReconciliationPayersAndYears, getReconciliationStates, getReconciliationListData, reconciliationUnSubmittedFormsExportToExcel, checkIfSelectedFormsCombinationAllowed } = {
    getReconciliationPayersAndYears: (payload) => {
        return {
            type: "RECONCILIATION/GET_PAYERS_AND_YEARS",
            payload: payload
        }
    },
    getReconciliationStates: (payload) => {
        return {
            type: "RECONCILIATION/GET_STATES",
            payload: payload
        }
    },
    getReconciliationListData: (payload) => {
        return {
            type: "RECONCILIATION/GET_LIST_DATA",
            payload: payload
        }
    },
    reconciliationUnSubmittedFormsExportToExcel: (payload) => {
        return {
            type: "RECONCILIATION/UNSUBMITTED_FORMS_EXPORT",
            payload: payload
        }
    },
    checkIfSelectedFormsCombinationAllowed: (payload) => {
        return {
            type: "RECONCILIATION/SELECTED_FORMS_COMBINATION_ALLOWED",
            payload: payload
        }
    },
}

function* getReconciliationPayersAndYearsAsync(action){
    try {
        yield put(setLoadingState({ state: PAGE_STATE.LOADING, message: "Getting data..." }));
        const [payerListResponse, taxYearListResponse] = yield all([
            call(ReconciliationDataService.getReconciliationPayers),
            call(ReconciliationDataService.getReconciliationTaxYears),
        ]);
        yield put(setLoadingState({ state: PAGE_STATE.PAGE_READY }));
        if(payerListResponse.data.Status){
            const reconciliationPayers = payerListResponse?.data?.Data;
            General.addOptionAndValueKeys(reconciliationPayers, "FullName", "PayerDetailId");
            yield put(setPayerDropdownOptions(reconciliationPayers));
        }else{
            yield put(addNotifications({
                heading: "Failed to get the payers",
                message: payerListResponse.data.Message,
                variant: TOASTER_VARIANT.DANGER
            }));
        }
        if(taxYearListResponse.data.Status){
            const reconciliationYearsList = taxYearListResponse?.data?.Data;
            General.addOptionAndValueKeys(reconciliationYearsList, "ID", "ID");
            yield put(setTaxYearDropdownOptions(reconciliationYearsList));
        }else{
            yield put(addNotifications({
                heading: "Failed to get the tax years",
                message: taxYearListResponse.data.Message,
                variant: TOASTER_VARIANT.DANGER
            }));
        }
    }catch(error) {
        console.log(error)
        yield put(addNotifications({
            heading: "Error",
            message: "We are facing some error. Please try again",
            variant: TOASTER_VARIANT.DANGER
        }));
    } finally {
        yield put(setLoadingState({ state: PAGE_STATE.PAGE_READY }));
    }
}

function* getReconciliationStatesAsync(action) {
    try {
        yield put(setLoadingState({ state: RECONCILIATION_LOADING_STATES.STATES_FILTER_LOADING }));
        const {payerId} = action.payload;
        const response = yield ReconciliationDataService.getReconciliationStates(payerId);
        
        if(response.data.Status){
            const reconciliationStates = response?.data?.Data;
            General.addOptionAndValueKeys(reconciliationStates, "StateName", "StateAbbreviation");
            yield put(setReconciliationStateDropdownOptions(reconciliationStates));
            yield put(setReconciliationSelectedState({label: "", value: ""}));
            yield put(setReconciliationFormsListRecords([]));
            yield put(setReconciliationSelectedFormRecords([]));
        }else{
            yield put(addNotifications({
                heading: "Failed to get the states",
                message: response.data.Message,
                variant: TOASTER_VARIANT.DANGER
            }));
        }
    }catch(error) {
        console.log(error)
        yield put(addNotifications({
            heading: "Error",
            message: "We are facing some error. Please try again",
            variant: TOASTER_VARIANT.DANGER
        }));
    } finally {
        yield put(setLoadingState({ state: RECONCILIATION_LOADING_STATES.STATES_FILTER_READY }));
    }
}

function* getReconciliationListDataAsync(action) {
    try {
        yield put(setLoadingState({ state: RECONCILIATION_LOADING_STATES.GRID_LOADING, message: "Getting records..." }));
        const response = yield ReconciliationDataService.getReconciliationListData(action.payload);
        
        if(response.data.Status){
            const reconciliationListData = response?.data?.Data;
            yield put(setReconciliationFormsListRecords(reconciliationListData));
            yield put(updatePaginationState({ total: reconciliationListData.length }))
        }else{
            yield put(addNotifications({
                heading: "Failed to get the records",
                message: response.data.Message,
                variant: TOASTER_VARIANT.DANGER
            }));
        }
    }catch(error) {
        console.log(error)
        yield put(addNotifications({
            heading: "Error",
            message: "We are facing some error. Please try again",
            variant: TOASTER_VARIANT.DANGER
        }));
    } finally {
        yield put(setLoadingState({ state: RECONCILIATION_LOADING_STATES.GRID_READY }));
    }
}

function* reconciliationUnSubmittedFormsExportToExcelAsync(action){
    try {
        yield put(setLoadingState({ state: RECONCILIATION_LOADING_STATES.DOWNLOAD_UNSUBMITTED_STATE_RECONCILLIATION_FORM_LOADING }));
        const response = yield ReconciliationDataService.getReconciliationUnSubmittedFormsExportToExcel();
        if(response){
            if(response?.data?.Status === 0){
                yield put(addNotifications({
                    heading: "Error",
                    message: response?.data?.Message,
                    variant: TOASTER_VARIANT.DANGER
                }));
            }else{
                const { Base64, Name } = response?.data?.Data;
                General.downloadFile(Base64, Name);
            }
        }
    } catch(error) {
        yield put(addNotifications({
            heading: "Error",
            message: error?.response?.data?.Message,
            variant: TOASTER_VARIANT.DANGER
        }));
    }finally {
        yield put(setLoadingState({ state: RECONCILIATION_LOADING_STATES.DOWNLOAD_UNSUBMITTED_STATE_RECONCILLIATION_FORM_READY }))
    }
}

function* checkIfSelectedFormsCombinationAllowedAsync(action){
    try {
        const {state, formNames} = action.payload;
        yield put(setLoadingState({ state: RECONCILIATION_LOADING_STATES.CHECK_IF_SELECTED_FORMS_COMBINATION_ALLOWED_LOADING }));
        const response = yield ReconciliationDataService.checkIfSelectedFormsCombinationAllowed(state, formNames);
        if(response.data){
            if(response?.data?.Status === 0){
                yield put(addNotifications({
                    heading: "Attention",
                    message: response?.data?.Message,
                    variant: TOASTER_VARIANT.DANGER
                }));
            }else{
                yield put(setIsSelectedFormsCombinationAllowed(true))
            }
        }
    } catch(error) {
        yield put(addNotifications({
            heading: "Error",
            message: error?.response?.data?.Message,
            variant: TOASTER_VARIANT.DANGER
        }));
    }finally {
        yield put(setLoadingState({ state: RECONCILIATION_LOADING_STATES.CHECK_IF_SELECTED_FORMS_COMBINATION_ALLOWED_READY }))
    }
}

function* rootSaga() {
    yield all([
        takeLatest(getReconciliationPayersAndYears().type, getReconciliationPayersAndYearsAsync),
        takeLatest(getReconciliationStates().type, getReconciliationStatesAsync),
        takeLatest(getReconciliationListData().type, getReconciliationListDataAsync),
        takeLatest(reconciliationUnSubmittedFormsExportToExcel().type, reconciliationUnSubmittedFormsExportToExcelAsync),
        takeLatest(checkIfSelectedFormsCombinationAllowed().type, checkIfSelectedFormsCombinationAllowedAsync),
    ]);
}
store.sagaManager.addSaga(componentKey, rootSaga);